import React, { useState } from 'react';
import { toast } from 'react-toastify';
import server from '../services/server';
import { formatCurrency } from '../helper/currency';

const RelatorioLayer = () => {
    const [filtros, setFiltros] = useState({
        tipo: 'clientes',
        dataInicio: '',
        dataFim: ''
    });
    const [carregando, setCarregando] = useState(false);
    const [visualizando, setVisualizando] = useState(false);
    const [dadosRelatorio, setDadosRelatorio] = useState(null);

    const handleFiltroChange = (campo, valor) => {
        setFiltros(prev => ({
            ...prev,
            [campo]: valor
        }));
    };

    const visualizarRelatorio = async () => {
        try {
            setVisualizando(true);
            
            // Construir query string com os filtros
            const params = new URLSearchParams();
            params.append('tipo', filtros.tipo);
            
            if (filtros.dataInicio) {
                params.append('dataInicio', filtros.dataInicio);
            }
            
            if (filtros.dataFim) {
                params.append('dataFim', filtros.dataFim);
            }

            const response = await server.get(`/relatorio/visualizar?${params.toString()}`);
            setDadosRelatorio(response.data);
            
            toast.success('Relatório carregado com sucesso!');
        } catch (error) {
            console.error('Erro ao visualizar relatório:', error);
            toast.error(error.response?.data?.error || 'Erro ao carregar relatório');
        } finally {
            setVisualizando(false);
        }
    };

    const baixarRelatorio = async () => {
        try {
            setCarregando(true);
            
            // Construir query string com os filtros
            const params = new URLSearchParams();
            params.append('tipo', filtros.tipo);
            
            if (filtros.dataInicio) {
                params.append('dataInicio', filtros.dataInicio);
            }
            
            if (filtros.dataFim) {
                params.append('dataFim', filtros.dataFim);
            }

            const response = await server.get(`/relatorio?${params.toString()}`, {
                responseType: 'blob'
            });

            // Criar blob e fazer download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `relatorio-${filtros.tipo}.pdf`;
            a.click();

            window.URL.revokeObjectURL(url);
            
            toast.success('Relatório baixado com sucesso!');
        } catch (error) {
            console.error('Erro ao baixar relatório:', error);
            
            // Tentar ler mensagem de erro se for JSON
            if (error.response?.data instanceof Blob) {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const errorData = JSON.parse(reader.result);
                        toast.error(errorData.error || 'Erro ao gerar relatório');
                    } catch {
                        toast.error('Erro ao gerar relatório');
                    }
                };
                reader.readAsText(error.response.data);
            } else {
                toast.error(error.message || 'Erro ao gerar relatório');
            }
        } finally {
            setCarregando(false);
        }
    };

    const getTituloRelatorio = () => {
        const tipos = {
            clientes: 'Relatório de Clientes',
            produtos: 'Relatório de Produtos',
            pedidos: 'Relatório de Pedidos'
        };
        return tipos[filtros.tipo] || 'Relatório';
    };

    const renderizarTabelaClientes = () => {
        if (!dadosRelatorio?.dados) return null;

        return (
            <div className="table-responsive">
                <table className="table table-striped table-hover">
                    <thead className="table-dark">
                        <tr>
                            <th>Nome</th>
                            <th>Email</th>
                            <th>Telefone</th>
                            <th>Documento</th>
                            <th>Data de Cadastro</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dadosRelatorio.dados.map((cliente, index) => (
                            <tr key={cliente.id}>
                                <td>{cliente.nome}</td>
                                <td>{cliente.email}</td>
                                <td>{cliente.telefone || 'Não informado'}</td>
                                <td>{cliente.documento || 'Não informado'}</td>
                                <td>{new Date(cliente.dataCriacao).toLocaleDateString('pt-BR')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderizarTabelaProdutos = () => {
        if (!dadosRelatorio?.dados) return null;

        return (
            <div className="table-responsive">
                <table className="table table-striped table-hover">
                    <thead className="table-dark">
                        <tr>
                            <th>Nome</th>
                            <th>Descrição</th>
                            <th>Valor</th>
                            <th>Quantidade</th>
                            <th>Data de Cadastro</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dadosRelatorio.dados.map((produto, index) => (
                            <tr key={produto.id}>
                                <td>{produto.nome}</td>
                                <td>{produto.descricao || 'Não informado'}</td>
                                <td>{formatCurrency(produto.valor)}</td>
                                <td>{produto.estoque ?? 'Não informado'}</td>
                                <td>{new Date(produto.dataCriacao).toLocaleDateString('pt-BR')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderizarTabelaPedidos = () => {
        if (!dadosRelatorio?.dados) return null;

        return (
            <div className="table-responsive">
                <table className="table table-striped table-hover">
                    <thead className="table-dark">
                        <tr>
                            <th>Cliente</th>
                            <th>Status</th>
                            <th>Data do Pedido</th>
                            <th>Itens</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dadosRelatorio.dados.map((pedido, index) => (
                            <tr key={pedido.id}>
                                <td>{pedido.cliente.nome}</td>
                                <td>
                                    <span className={`badge bg-${pedido.status === 'FINALIZADO' ? 'success' : 
                                                   pedido.status === 'EM_PRODUCAO' ? 'warning' : 
                                                   pedido.status === 'CANCELADO' ? 'danger' : 'primary'}`}>
                                        {pedido.status}
                                    </span>
                                </td>
                                <td>{new Date(pedido.dataCriacao).toLocaleDateString('pt-BR')}</td>
                                <td>
                                    {(pedido.pedidoItems || pedido.item || []).length > 0 ? (
                                        <ul className="list-unstyled mb-0">
                                            {(pedido.pedidoItems || pedido.item || []).map((item, itemIndex) => (
                                                <li key={itemIndex}>
                                                    {item.produto?.nome || item.nomeItem} - Qtd: {item.quantidade} - {formatCurrency(item.valorTotal)}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        'Nenhum item'
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderizarTabela = () => {
        switch (filtros.tipo) {
            case 'clientes':
                return renderizarTabelaClientes();
            case 'produtos':
                return renderizarTabelaProdutos();
            case 'pedidos':
                return renderizarTabelaPedidos();
            default:
                return null;
        }
    };

    return (
        <div className='card p-4'>
            <div className="row">
                <div className="col-12">
                    <h6 className="mb-4">{getTituloRelatorio()}</h6>
                </div>
            </div>

            <div className="row">
                <div className="col-md-4 mb-3">
                    <label htmlFor="tipo" className="form-label">Tipo de Relatório</label>
                    <select
                        id="tipo"
                        className="form-select"
                        value={filtros.tipo}
                        onChange={(e) => handleFiltroChange('tipo', e.target.value)}
                    >
                        <option value="clientes">Clientes</option>
                        <option value="produtos">Produtos</option>
                        <option value="pedidos">Pedidos</option>
                    </select>
                </div>

                <div className="col-md-4 mb-3">
                    <label htmlFor="dataInicio" className="form-label">Data de Início</label>
                    <input
                        type="date"
                        id="dataInicio"
                        className="form-control"
                        value={filtros.dataInicio}
                        onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
                    />
                </div>

                <div className="col-md-4 mb-3">
                    <label htmlFor="dataFim" className="form-label">Data de Fim</label>
                    <input
                        type="date"
                        id="dataFim"
                        className="form-control"
                        value={filtros.dataFim}
                        onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="alert alert-info">
                        <strong>Instruções:</strong>
                        <ul className="mb-0 mt-2">
                            <li>Selecione o tipo de relatório desejado</li>
                            <li>Opcionalmente, defina um período específico usando as datas</li>
                            <li>Use "Visualizar" para ver os dados na tela</li>
                            <li>Use "Baixar PDF" para gerar o relatório em PDF</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="btn-group" role="group">
                        <button 
                            onClick={visualizarRelatorio} 
                            className='btn btn-info me-2'
                            disabled={visualizando}
                        >
                            {visualizando ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Carregando...
                                </>
                            ) : (
                                <>
                                    <i className="ri-eye-line me-2"></i>
                                    Visualizar
                                </>
                            )}
                        </button>
                        
                        <button 
                            onClick={baixarRelatorio} 
                            className='btn btn-primary'
                            disabled={carregando}
                        >
                            {carregando ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Gerando PDF...
                                </>
                            ) : (
                                <>
                                    <i className="ri-download-line me-2"></i>
                                    Baixar PDF
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* {filtros.dataInicio && filtros.dataFim && (
                <div className="row mt-3">
                    <div className="col-12">
                        <div className="alert alert-warning">
                            <strong>Filtro Aplicado:</strong> Período de {new Date(filtros.dataInicio).toLocaleDateString('pt-BR')} a {new Date(filtros.dataFim).toLocaleDateString('pt-BR')}
                        </div>
                    </div>
                </div>
            )} */}

            {/* Área de visualização dos dados */}
            {dadosRelatorio && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    {dadosRelatorio.titulo}
                                    <span className="badge bg-secondary ms-2">{dadosRelatorio.totalRegistros} registros</span>
                                </h5>
                                <small className="text-muted">
                                    Gerado em: {new Date(dadosRelatorio.dataGeracao).toLocaleString('pt-BR')}
                                </small>
                            </div>
                            <div className="card-body">
                                {renderizarTabela()}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RelatorioLayer;
